import { Button, DataGridFilterField, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fromDataGridFilterValue,
  isNotEmptyFilterValue,
  NOT_EMPTY_FILTER_VALUE,
  toDataGridFilterValue,
} from '@/shared/lib';
import { FiltersDescriptionValues } from './FiltersDescriptionValues';
import type { DescriptionFilterEditor } from './model/filtersDescription';

/** Пропсы значений фильтра, открывающих его прямо из описания отбора */
type Props = {
  /** Колонка, фильтр которой открывается */
  columnId: string;
  /** Чем и с каким значением открывать фильтр */
  editor: DescriptionFilterEditor;
  /** Показанные значения фильтра — ими и подписана кнопка */
  text: string;
  /** Сколько значений свёрнуто в счётчик */
  foldedCount: number;
  /** Предел длины текста значений в знаках; `null` — предела нет */
  limitChars: number | null;
  /** Записывает значение фильтра колонки */
  onChange: (value: unknown) => void;
};

/** Значения фильтров сравниваются по составу: правка без изменений таблицу не трогает */
const isSameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Открывает фильтр колонки нажатием на само значение в описании отбора: в окне — то же поле, что
 * и в шапке колонки, с теми же кнопками применения и сброса. Правка живёт черновиком и уходит в
 * таблицу применением; закрытие окна мимо кнопок её отменяет.
 */
export const FiltersDescriptionFilter: FC<Props> = ({ columnId, editor, text, foldedCount, limitChars, onChange }) => {
  const { t } = useTranslation(['common']);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<unknown>(editor.value);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Правка начинается с действующего значения фильтра — каждое открытие с него же
    if (open) setDraft(editor.value);
  };

  const applyDraft = () => {
    setIsOpen(false);
    if (!isSameValue(draft, editor.value)) onChange(draft);
  };

  const resetFilter = () => {
    setIsOpen(false);
    onChange(undefined);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {/* Пробелы внутри кнопки задаёт сам текст значений, поэтому промежутка у кнопки нет */}
        <Button variant="ghost" size="sm" className="min-w-0 gap-0 px-2 font-normal">
          <FiltersDescriptionValues text={text} foldedCount={foldedCount} limitChars={limitChars} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        {editor.render ? (
          editor.render({
            columnId,
            value: draft,
            // Признак заполненности живёт в самом черновике значением: им же он и сохраняется
            operator: isNotEmptyFilterValue(draft) ? 'notEmpty' : undefined,
            setOperator: (operator) => setDraft(operator === 'notEmpty' ? NOT_EMPTY_FILTER_VALUE : undefined),
            // Правка здесь черновая до применения, поэтому запись значения и запись черновика —
            // одно и то же: в таблицу набранное уходит кнопкой «Применить».
            setValue: setDraft,
            setDraft,
            apply: applyDraft,
            close: () => setIsOpen(false),
          })
        ) : editor.config ? (
          <DataGridFilterField
            config={editor.config}
            // DataGridFilterField работает с конвертом { value, operator }, а черновик здесь и в
            // editor.render хранит значение отбора как есть — оборачиваем/разворачиваем на границе
            value={toDataGridFilterValue(draft)}
            onChange={(next) => setDraft(fromDataGridFilterValue(next))}
            className="min-w-56 p-2"
          />
        ) : null}
        {/* Поле фильтра вне грида своих кнопок не имеет — их ставит страница, теми же подписями */}
        <div className="flex justify-end gap-2 border-t p-2">
          <Button variant="ghost" size="sm" onClick={resetFilter}>
            {t('common:clear')}
          </Button>
          <Button size="sm" onClick={applyDraft}>
            {t('common:accept')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
